import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Tag, 
  Space, 
  Input, 
  Select, 
  message,
  Modal,
  Typography
} from 'antd';
import {
  SearchOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  SoundOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { AudioAsset, AudioAssetType } from '../../types';
import { EditAudioModal } from './EditAudioModal';
import { GenerateAudioModal } from './GenerateAudioModal';
import { GenerateFromFilmModal } from './GenerateFromFilmModal';
import { GenerateSfxModal } from './GenerateSfxModal';

const { Option } = Select;

export const AudioLibrary: React.FC = () => {
  const [data, setData] = useState<AudioAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [genreFilter, setGenreFilter] = useState<string | undefined>();
  const [moodFilter, setMoodFilter] = useState<string | undefined>();
  const [instrumentsFilter, setInstrumentsFilter] = useState<string | undefined>();
  const [typeFilter, setTypeFilter] = useState<AudioAssetType | undefined>();
  const [sortBy, setSortBy] = useState('createdAt_desc');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [filmModalVisible, setFilmModalVisible] = useState(false);
  const [sfxModalVisible, setSfxModalVisible] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<AudioAsset | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [filterOptions, setFilterOptions] = useState<{ genres: string[]; moods: string[]; instruments: string[] }>({ genres: [], moods: [], instruments: [] });

  useEffect(() => {
    audioLibraryApi.getFilterOptions()
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageSize,
        pageIndex,
        sortBy
      };
      
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (genreFilter) params.genre = genreFilter;
      if (moodFilter) params.mood = moodFilter;
      if (instrumentsFilter) params.instruments = instrumentsFilter;

      const result = await audioLibraryApi.findMany(params);
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error: any) {
      message.error('Failed to load audio assets: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pageIndex, pageSize, search, typeFilter, genreFilter, moodFilter, instrumentsFilter, sortBy]);

  const handlePlayPause = (audio: AudioAsset) => {
    if (playingAudioId === audio.id && audioElement && !audioElement.paused) {
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      if (audioElement) {
        audioElement.pause();
      }
      const newAudio = new Audio(audio.url);
      newAudio.play();
      setAudioElement(newAudio);
      setPlayingAudioId(audio.id);
      
      newAudio.onended = () => {
        setPlayingAudioId(null);
      };
    }
  };

  const handleEdit = (audio: AudioAsset) => {
    setSelectedAudio(audio);
    setEditModalVisible(true);
  };

  const handleDelete = (audio: AudioAsset) => {
    Modal.confirm({
      title: 'Archive Audio',
      content: `Are you sure you want to archive "${audio.name}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await audioLibraryApi.deleteAudio(audio.id);
          message.success('Audio archived successfully');
          loadData();
        } catch (error: any) {
          message.error('Failed to archive audio: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const columns: ColumnsType<AudioAsset> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{text}</Typography.Text>
          {record.description && (
            <Typography.Text 
              type="secondary" 
              style={{ fontSize: '12px' }}
              ellipsis={{ tooltip: record.description }}
            >
              {record.description}
            </Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type: string) => (
        <Tag color={type === 'sound_effect' ? 'orange' : 'green'}>
          {type === 'sound_effect' ? 'Sound Effect' : 'Music'}
        </Tag>
      )
    },
    {
      title: 'Genre',
      dataIndex: 'genre',
      key: 'genre',
      width: 120,
      render: (genre) => genre ? <Tag color="blue">{genre}</Tag> : '-'
    },
    {
      title: 'Instruments',
      dataIndex: 'instruments',
      key: 'instruments',
      width: 180,
      render: (instruments: string[]) => instruments?.length > 0 
        ? instruments.slice(0, 3).map(i => <Tag key={i}>{i}</Tag>)
        : '-'
    },
    {
      title: 'Mood',
      dataIndex: 'mood',
      key: 'mood',
      width: 140,
      render: (mood: string[]) => mood?.length > 0 
        ? mood.map(m => <Tag key={m} color="purple">{m}</Tag>)
        : '-'
    },
    {
      title: 'BPM',
      dataIndex: 'bpm',
      key: 'bpm',
      width: 70,
      render: (bpm) => bpm || '-'
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration) => formatDuration(duration)
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 90,
      render: (size) => formatFileSize(size)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={playingAudioId === record.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handlePlayPause(record)}
          />
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={async () => {
              try {
                await audioLibraryApi.download(record.id, `${record.name}.${record.format || 'wav'}`);
              } catch {
                message.error('Failed to download audio');
              }
            }}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={2}>Audio Library</Typography.Title>
        <Space>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => setGenerateModalVisible(true)}
          >
            Generate Audio
          </Button>
          <Button
            icon={<SoundOutlined />}
            onClick={() => setSfxModalVisible(true)}
          >
            Generate SFX
          </Button>
          <Button
            icon={<VideoCameraOutlined />}
            onClick={() => setFilmModalVisible(true)}
          >
            Generate from Film
          </Button>
        </Space>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Search by name, description, genre..."
          prefix={<SearchOutlined />}
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Select
          placeholder="Type"
          style={{ width: 140 }}
          value={typeFilter}
          onChange={setTypeFilter}
          allowClear
        >
          <Option value="music">Music</Option>
          <Option value="sound_effect">Sound Effect</Option>
        </Select>
        <Select
          placeholder="Genre"
          style={{ width: 150 }}
          value={genreFilter}
          onChange={setGenreFilter}
          allowClear
          showSearch
        >
          {filterOptions.genres.map(g => (
            <Option key={g} value={g}>{g}</Option>
          ))}
        </Select>
        <Select
          placeholder="Mood"
          style={{ width: 150 }}
          value={moodFilter}
          onChange={setMoodFilter}
          allowClear
          showSearch
        >
          {filterOptions.moods.map(m => (
            <Option key={m} value={m}>{m}</Option>
          ))}
        </Select>
        <Select
          placeholder="Instruments"
          style={{ width: 160 }}
          value={instrumentsFilter}
          onChange={setInstrumentsFilter}
          allowClear
          showSearch
        >
          {filterOptions.instruments.map(i => (
            <Option key={i} value={i}>{i}</Option>
          ))}
        </Select>
        <Select
          value={sortBy}
          onChange={setSortBy}
          style={{ width: 150 }}
        >
          <Option value="createdAt_desc">Newest First</Option>
          <Option value="createdAt_asc">Oldest First</Option>
          <Option value="name_asc">Name A-Z</Option>
          <Option value="name_desc">Name Z-A</Option>
          <Option value="duration_asc">Duration (Short)</Option>
          <Option value="duration_desc">Duration (Long)</Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pageIndex + 1,
          pageSize: pageSize,
          total: total,
          onChange: (page, size) => {
            setPageIndex(page - 1);
            setPageSize(size || 20);
          },
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`
        }}
      />

      <GenerateAudioModal
        visible={generateModalVisible}
        onClose={() => setGenerateModalVisible(false)}
        onSuccess={() => {
          setGenerateModalVisible(false);
          loadData();
        }}
      />

      <GenerateSfxModal
        visible={sfxModalVisible}
        onClose={() => setSfxModalVisible(false)}
        onSuccess={() => {
          setSfxModalVisible(false);
          loadData();
        }}
      />

      <GenerateFromFilmModal
        visible={filmModalVisible}
        onClose={() => setFilmModalVisible(false)}
        onSuccess={() => {
          setFilmModalVisible(false);
          loadData();
        }}
      />

      {selectedAudio && (
        <EditAudioModal
          visible={editModalVisible}
          audio={selectedAudio}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedAudio(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedAudio(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default AudioLibrary;
